import styled from "styled-components"

import { Checkbox, spacing, TextField } from "@minuthq/meatball-ui-react"
import { useFormik } from "formik"

import { usePatchWarehouse } from "src/data/ecommerce/ecommerceQueries"
import { TWarehouse } from "src/data/ecommerce/ecommerceTypes"
import { MDialog } from "src/ui/Dialog/MDialog"

const EDIT_WAREHOUSE_INVENTORY_FORM_ID = "edit_warehouse_inventory_form"

type TWarehouseArticle = TWarehouse["articles"][number]

type TEditWarehouseInventoryForm = {
  low_stock_threshold: number
  out_of_stock_threshold: number
  allow_selling_when_out_of_stock: boolean
}

export function ParadiseEditWarehouseInventoryDialog({
  warehouse,
  article,
  onClose,
}: {
  warehouse: TWarehouse
  article: TWarehouseArticle
  onClose: () => void
}) {
  const patchWarehouse = usePatchWarehouse()

  const form = useFormik<TEditWarehouseInventoryForm>({
    initialValues: {
      low_stock_threshold: article.low_stock_threshold,
      out_of_stock_threshold: article.out_of_stock_threshold,
      allow_selling_when_out_of_stock:
        article.allow_selling_when_out_of_stock ?? false,
    },
    onSubmit: updateWarehouse,
  })

  function updateWarehouse() {
    patchWarehouse.mutate(
      {
        path: { warehouse_id: warehouse.id },
        body: {
          articles: [
            {
              sku: article.sku,
              low_stock_threshold: form.values.low_stock_threshold,
              out_of_stock_threshold: form.values.out_of_stock_threshold,
              allow_selling_when_out_of_stock:
                form.values.allow_selling_when_out_of_stock,
            },
          ],
        },
      },
      {
        onSuccess: onClose,
      }
    )
  }

  return (
    <MDialog
      open={true}
      onClose={onClose}
      title={`Edit SKU: ${article.sku}`}
      formId={EDIT_WAREHOUSE_INVENTORY_FORM_ID}
      error={patchWarehouse.isError && "Failed to update warehouse inventory"}
      loading={patchWarehouse.isPending}
      confirmLabel="Save"
      closeLabel="Cancel"
    >
      <form id={EDIT_WAREHOUSE_INVENTORY_FORM_ID} onSubmit={form.handleSubmit}>
        <FormWrapper>
          <TextField
            label="Low Stock Threshold"
            type="number"
            value={form.values.low_stock_threshold.toString()}
            onChange={(value) =>
              form.setValues((prev) => ({
                ...prev,
                low_stock_threshold: Number(value),
              }))
            }
            error={form.errors.low_stock_threshold}
            required
          />
          <TextField
            label="Out of Stock Threshold"
            type="number"
            value={form.values.out_of_stock_threshold.toString()}
            onChange={(value) =>
              form.setValues((prev) => ({
                ...prev,
                out_of_stock_threshold: Number(value),
              }))
            }
            error={form.errors.out_of_stock_threshold}
            required
          />
          <Checkbox
            label="Allow selling when out of stock"
            checked={form.values.allow_selling_when_out_of_stock}
            onCheck={(checked) =>
              form.setValues((prev) => ({
                ...prev,
                allow_selling_when_out_of_stock: checked,
              }))
            }
          />
        </FormWrapper>
      </form>
    </MDialog>
  )
}

const FormWrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`
